import React, { useState, useEffect, useMemo } from 'react'
import { GroupMember } from '@/api/types'
import { ConversationInfo, Role } from '@/api/types/chat'
import { tabStateManager } from '@/utils/tabStateManager'
import { useGroupStore } from '@/stores/useGroupStore'
import { useGroupActions } from '@/hooks/useGroupActions'

// @ 用户的联合类型定义
type AtUser = GroupMember | { id: 'all'; nickname: '所有人'; avatar: ''; handle: 0; role: 'member' }

interface AtSelectorPopupProps {
  conversation: ConversationInfo
  onSelectUser: (user: AtUser) => void
  onClose: () => void
  searchTerm: string
  position: { x: number; y: number }
}

export default function AtSelectorPopup({
  conversation,
  onSelectUser,
  onClose,
  searchTerm,
  position
}: AtSelectorPopupProps): React.JSX.Element {
  const [filteredMembers, setFilteredMembers] = useState<GroupMember[]>([])
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [groupMembers, setGroupMembers] = useState<GroupMember[]>([])

  // 获取群组数据和操作方法
  const { getGroupMembers, isLoading } = useGroupActions()

  // 获取当前群组ID
  const currentGroupId = useMemo(() => {
    return useGroupStore.getState().groupMap.get(conversation.id)?.id ?? ""
  }, [conversation.id])

  // 获取群组成员信息
  useEffect(() => {
    const loadGroupMembers = async () => {
      if (!currentGroupId) return

      // 首先尝试从 store 中获取缓存的数据
      const cachedMembers = useGroupStore.getState().groupInfoMap.get(currentGroupId)?.participants
      if (cachedMembers && cachedMembers.length > 0) {
        setGroupMembers(cachedMembers)
        return
      }

      // 如果没有缓存，则从接口获取
      try {
        const members = await getGroupMembers(currentGroupId)
        setGroupMembers(members)
      } catch (error) {
        console.error('获取群组成员失败:', error)
        setGroupMembers([])
      }
    }

    loadGroupMembers()
  }, [currentGroupId])

  // 检查当前用户是否为群组管理员
  const isGroupAdmin = useMemo(() => {
    if (conversation.session_type !== 'group') {
      return false
    }

    const currentUserId = tabStateManager.getUserId()
    const currentUserParticipant = conversation.participants?.find(p => p.id === currentUserId)
    return currentUserParticipant?.role === Role.Owner
  }, [conversation])

  // 筛选群组成员列表
  useEffect(() => {
    // 排除当前用户
    const currentUserId = tabStateManager.getUserId()
    const otherMembers = groupMembers.filter(member => member.id !== currentUserId)

    if (!searchTerm) {
      setFilteredMembers(otherMembers.slice(0, 8)) // 限制显示数量
    } else {
      const filtered = otherMembers.filter(member =>
        member.nickname.toLowerCase().includes(searchTerm.toLowerCase())
      ).slice(0, 8) // 限制显示数量
      setFilteredMembers(filtered)
    }

    setSelectedIndex(0) // 重置选中索引
  }, [groupMembers, searchTerm])

  // 处理键盘导航
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const totalItems = filteredMembers.length + (isGroupAdmin ? 1 : 0)

      switch (e.key) {
        case 'ArrowUp':
          e.preventDefault()
          setSelectedIndex(prev => (prev - 1 + totalItems) % totalItems)
          break
        case 'ArrowDown':
          e.preventDefault()
          setSelectedIndex(prev => (prev + 1) % totalItems)
          break
        case 'Enter':
          e.preventDefault()
          handleSelectItem()
          break
        case 'Escape':
          e.preventDefault()
          onClose()
          break
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [filteredMembers.length, isGroupAdmin, selectedIndex])

  // 处理选择项目
  const handleSelectItem = () => {
    if (isGroupAdmin && selectedIndex === 0) {
      // 选择 @所有人
      onSelectUser({ id: 'all', nickname: '所有人', avatar: '', handle: 0, role: 'member' })
    } else {
      // 选择群组成员
      const memberIndex = isGroupAdmin ? selectedIndex - 1 : selectedIndex
      const selectedMember = filteredMembers[memberIndex]
      if (selectedMember) {
        onSelectUser(selectedMember)
      }
    }
  }

  // 计算弹出框位置
  const popupStyle: React.CSSProperties = {
    position: 'fixed',
    left: position.x,
    bottom: window.innerHeight - position.y + 10, // 在输入框上方显示
    zIndex: 1000,
    maxHeight: '200px',
    minWidth: '200px'
  }

  const totalItems = filteredMembers.length + (isGroupAdmin ? 1 : 0)

  // 显示加载状态
  if (isLoading) {
    return (
      <div style={popupStyle} className="bg-white border border-gray-200 rounded-lg shadow-lg p-4">
        <div className="flex items-center space-x-2">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
          <div className="text-gray-500 text-sm">加载群组成员...</div>
        </div>
      </div>
    )
  }

  if (totalItems === 0) {
    return (
      <div style={popupStyle} className="bg-white border border-gray-200 rounded-lg shadow-lg p-4">
        <div className="text-gray-500 text-sm">没有找到匹配的群组成员</div>
      </div>
    )
  }

  return (
    <div style={popupStyle} className="bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
      <div className="max-h-48 overflow-y-auto">
        {/* @所有人选项（仅管理员可见） */}
        {isGroupAdmin && (
          <div
            className={`flex items-center space-x-3 px-4 py-3 cursor-pointer ${selectedIndex === 0 ? 'bg-blue-50 text-blue-600' : 'hover:bg-gray-50'
              }`}
            onClick={() => onSelectUser({ id: 'all', nickname: '所有人', avatar: '', handle: 0, role: 'member' })}
          >
            <div className="w-8 h-8 rounded-full bg-orange-500 flex items-center justify-center">
              <span className="text-white text-xs font-medium">全</span>
            </div>
            <div>
              <div className="text-sm font-medium">所有人</div>
              <div className="text-xs text-gray-500">@所有群成员</div>
            </div>
          </div>
        )}

        {/* 群组成员列表 */}
        {filteredMembers.map((member, index) => {
          const actualIndex = isGroupAdmin ? index + 1 : index
          return (
            <div
              key={member.id}
              className={`flex items-center space-x-3 px-4 py-3 cursor-pointer ${selectedIndex === actualIndex ? 'bg-blue-50 text-blue-600' : 'hover:bg-gray-50'
                }`}
              onClick={() => onSelectUser(member)}
            >
              {member.avatar ? (
                <img
                  src={member.avatar}
                  alt={member.nickname}
                  className="w-8 h-8 rounded-full object-cover"
                />
              ) : (
                <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center">
                  <span className="text-white text-xs font-medium">
                    {member.nickname.charAt(0).toUpperCase()}
                  </span>
                </div>
              )}
              <div>
                <div className="text-sm font-medium flex items-center">
                  {member.nickname}
                  {member.role === 'owner' && (
                    <span className="ml-2 text-xs bg-red-100 text-red-600 px-1 rounded">群主</span>
                  )}
                  {member.role === 'admin' && (
                    <span className="ml-2 text-xs bg-blue-100 text-blue-600 px-1 rounded">管理员</span>
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* 底部提示 */}
      <div className="px-4 py-2 bg-gray-50 border-t border-gray-100">
        <div className="text-xs text-gray-500">
          ↑↓ 选择 • Enter 确认 • Esc 取消
        </div>
      </div>
    </div>
  )
}
